/**
 * Outbox rules — the decisions, with no IndexedDB and no network.
 *
 * Ported from Csa/offline/outboxRules.js. This logic is domain-agnostic (it
 * only ever talks about "entity"/"entity_key"/"seq"), so nothing here changed
 * except the doc comments' examples - the key construction, backoff,
 * ordering and acknowledgement rules are identical to CSA's by design: this
 * is the one layer where Electrical shouldn't diverge from the pattern that
 * already works.
 *
 * Everything in this file is a pure function, so the parts of offline sync
 * that are easy to get subtly wrong — key construction, sequence numbers,
 * backoff, what an acknowledgement means — are unit-testable without a
 * browser. The storage layer (electricalDb.js) and the orchestration
 * (electricalRepo.js) stay dumb on purpose, because neither can be tested
 * the same way.
 */

/* ------------------------------------------------------------------ */
/* identity                                                            */
/* ------------------------------------------------------------------ */

/**
 * A client-side id that is stable before a row reaches the server.
 *
 * `crypto.randomUUID` is present on every browser that supports the rest of
 * this feature, but only in a secure context — so a developer on plain
 * http://192.168.x.x would silently get undefined. The fallback keeps the
 * same shape and the same guarantee, which is only "unique within one
 * auditor's device".
 */
export function newLocalId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch (e) {
      /* fall through */
    }
  }
  const hex = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += "-";
    else if (i === 14) out += "4";
    else out += hex[Math.floor(Math.random() * 16)];
  }
  return out;
}

/**
 * The idempotency key: (audit_id, entity, entity_key, client_seq).
 *
 * The server refuses a key that does not begin with the audit id, so that a
 * client cannot burn a key belonging to a different audit. Colons separate
 * the parts, so they are stripped from the entity key — a checkpoint code
 * never contains one, but an area/category name could, and a key that
 * parses ambiguously is worse than a key that is merely ugly.
 */
export function idempotencyKey(auditId, entity, entityKey, seq) {
  const safe = String(entityKey == null ? "" : entityKey).replace(/:/g, "_").slice(0, 80);
  return `${auditId}:${entity}:${safe}:${seq}`;
}

/**
 * What identifies the thing an operation acts on.
 *
 * Two operations on the same entity key are ordered against each other and
 * their keys differ only by seq. Anything the server addresses by local_id
 * uses that; answers use the checkpoint code.
 */
export function entityKeyFor(entity, payload) {
  const p = payload || {};
  if (entity === "answer") return p.checkpoint_code || p.level_value || "";
  if (entity === "exclusion") return p.level_value || "";
  return p.local_id || "";
}

/* ------------------------------------------------------------------ */
/* queueing                                                            */
/* ------------------------------------------------------------------ */

/**
 * Build the queue entry for one local write.
 *
 * `seq` is monotonic per audit and never reused, which is what makes the key
 * stable across retries: the same local write always produces the same key,
 * so replaying it is recognised rather than applied twice.
 */
export function makeEntry({ auditId, entity, op, payload, seq, now }) {
  const entityKey = entityKeyFor(entity, payload);
  return {
    audit_id: auditId,
    seq,
    key: idempotencyKey(auditId, entity, entityKey, seq),
    entity,
    op,
    entity_key: entityKey,
    payload,
    tries: 0,
    next_try_at: 0,
    last_error: null,
    created_at: now == null ? Date.now() : now,
  };
}

/**
 * Which entries to send, in which order.
 *
 * Ordering is by seq. Entries still in backoff are held, and — importantly —
 * so is everything queued behind them *for the same entity key*, because
 * those carry row_versions that assume the earlier write landed. Other
 * entities are unaffected: a failed operation blocks nothing except its own
 * entity.
 */
export function selectBatch(entries, { now, limit = 200 } = {}) {
  const t = now == null ? Date.now() : now;
  const ordered = [...entries].sort((a, b) => a.seq - b.seq);

  const blocked = new Set();
  const out = [];

  for (const e of ordered) {
    const lane = e.entity + ":" + e.entity_key;
    if (blocked.has(lane)) continue;

    if (e.next_try_at && e.next_try_at > t) {
      blocked.add(lane);
      continue;
    }

    out.push(e);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Exponential backoff with jitter, capped at five minutes.
 *
 * Jitter matters more than it looks: without it, thirty tablets reconnecting
 * to the office wifi at the end of a site visit retry in lockstep.
 */
export function backoffMs(tries, rand) {
  const base = Math.min(1000 * Math.pow(2, Math.max(0, tries - 1)), 300000);
  const r = typeof rand === "function" ? rand() : Math.random();
  return Math.round(base * (0.75 + r * 0.5));
}

/* ------------------------------------------------------------------ */
/* acknowledgement                                                     */
/* ------------------------------------------------------------------ */

/**
 * Decide what happens to each queued entry, given the server's acks.
 *
 * The client clears an outbox entry only on an explicit ack — so an entry the
 * server said nothing about is kept, not assumed lost and not assumed
 * applied. That is the case a naive implementation gets wrong, and it is the
 * one that loses an auditor's work.
 *
 * @returns { clear: [key], retry: [{key, tries, next_try_at, last_error}],
 *            conflicts: [ack], applied: [ack] }
 */
export function applyAcks(entries, results, { now, rand } = {}) {
  const t = now == null ? Date.now() : now;
  const byKey = new Map((results || []).map((r) => [r.key, r]));

  const clear = [];
  const retry = [];
  const conflicts = [];
  const applied = [];

  for (const e of entries) {
    const ack = byKey.get(e.key);

    // No ack: keep it. The next flush sends it again, and the key makes that
    // safe whether or not it actually landed.
    if (!ack) continue;

    if (ack.status === "applied" || ack.status === "already_applied") {
      clear.push(e.key);
      applied.push(ack);
      continue;
    }

    if (ack.status === "conflict") {
      // Held, not discarded and not retried on a timer: a conflict needs the
      // auditor to decide, so it surfaces in the UI and waits.
      conflicts.push({ ...ack, entry: e });
      continue;
    }

    const tries = (e.tries || 0) + 1;
    retry.push({
      key: e.key,
      tries,
      next_try_at: t + backoffMs(tries, rand),
      last_error: ack.message || "Could not apply",
    });
  }

  return { clear, retry, conflicts, applied };
}

/**
 * A one-line summary for the offline indicator.
 *
 * Conflicts are counted separately because they are the only state the
 * auditor has to act on — a pending count that quietly includes them reads
 * as "still working" when it means "stuck".
 */
export function summarise(entries, { online, syncing, now, photosPending } = {}) {
  const t = now == null ? Date.now() : now;
  const pending = entries.length;
  const waiting = entries.filter((e) => e.next_try_at && e.next_try_at > t).length;
  const failing = entries.filter((e) => (e.tries || 0) >= 3).length;

  /*
   * Photographs waiting to upload count as unsynced work.
   *
   * They are not outbox entries — they live in their own store with an
   * upload_state — so counting only `entries` would report "synced" while
   * evidence was still sitting on the device. Submit is gated on this
   * number too.
   */
  const photos = Number(photosPending) || 0;

  let state;
  if (!online) state = "offline";
  else if (syncing) state = "syncing";
  else if (failing) state = "failing";
  else if (pending || photos) state = "pending";
  else state = "synced";

  return { state, pending, waiting, failing, photos_pending: photos, online: !!online };
}

/* ------------------------------------------------------------------ */
/* local application                                                   */
/* ------------------------------------------------------------------ */

/**
 * Apply an answer write to the locally-held row.
 *
 * row_version is bumped locally so that a second edit queues the version the
 * first one will have produced. That is what lets several edits to the same
 * checkpoint be made offline and replay in order without a false conflict —
 * the versions the client sends form the same sequence the server will walk.
 */
export function applyAnswerLocally(row, patch, userId, now) {
  const base = row || { row_version: 0 };
  const next = { ...base };

  if (patch.criticality !== undefined) next.criticality = patch.criticality;
  if (patch.status !== undefined) next.status = patch.status;
  if (patch.observation !== undefined) next.observation = patch.observation;
  // elec_answer.recommendation is singular - the checkpoint's own template
  // text (elec_checkpoint.recommendations) is plural, a different field
  // entirely. Checking the plural name here meant a Recommendation write
  // never updated the local row at all while offline, let alone queued a
  // sync for it.
  if (patch.recommendation !== undefined) {
    next.recommendation = patch.recommendation;
    next.recommendation_edited = true;
  }
  if (patch.legal_reference !== undefined) next.legal_reference = patch.legal_reference;
  if (patch.set_by_bulk !== undefined) next.set_by_bulk = !!patch.set_by_bulk;

  next.answered_by = userId;
  next.answered_at = now == null ? new Date().toISOString() : now;
  next.row_version = (base.row_version || 0) + 1;
  next.dirty = true;
  return next;
}

/**
 * Adopt the server's version of a row after it acknowledges the write.
 *
 * Only if nothing was typed in the meantime: the ack describes the state as
 * of the write that was sent, and a later local edit is newer than that.
 * Clearing `dirty` is conditional on the versions lining up for the same
 * reason.
 */
export function reconcileAnswer(local, acked) {
  if (!acked) return local;
  if (!local) return { ...acked, dirty: false };

  if ((local.row_version || 0) > (acked.row_version || 0)) return local;
  return { ...acked, dirty: false };
}
